import React from 'react';
import { Link } from 'react-router-dom';
import { submitArenaResult, getArenaLeaderboard, getArenaStats } from '../services/arenaService';

// ─── Difficulty Config ────────────────────────────────────────────────────────

export const MAZE_DIFFICULTY = {
  Easy:   { rows: 9,  cols: 9, label: 'Easy' },
  Medium: { rows: 13, cols: 13, label: 'Medium' },
  Hard:   { rows: 17, cols: 17, label: 'Hard' },
};

export const TODAY_MAZE = {
  title: 'Neon Maze',
  type: 'maze',
  difficulty: 'Medium',
  category: 'Navigation',
};

// ─── Seeded RNG (mulberry32) ──────────────────────────────────────────────────

function seededRng(seed) {
  let s = seed;
  return () => {
    s |= 0;
    s = s + 0x6d2b79f5 | 0;
    let t = Math.imul(s ^ s >>> 15, 1 | s);
    t = t + Math.imul(t ^ t >>> 7, 61 | t) ^ t;
    return ((t ^ t >>> 14) >>> 0) / 4294967296;
  };
}

function getDailySeed() {
  const d = new Date();
  return d.getFullYear() * 10000 + (d.getMonth() + 1) * 100 + d.getDate();
}

// ─── Maze Generation (DFS + guaranteed second path) ─────────────────────────
export function generateMaze(rows, cols, seed) {
  const rng = seededRng(seed ?? getDailySeed());

  const dirs = [
    { dr: -1, dc:  0, wall: 'top',    opposite: 'bottom' },
    { dr:  1, dc:  0, wall: 'bottom', opposite: 'top'    },
    { dr:  0, dc: -1, wall: 'left',   opposite: 'right'  },
    { dr:  0, dc:  1, wall: 'right',  opposite: 'left'   },
  ];

  function shuffle(arr) {
    for (let i = arr.length - 1; i > 0; i--) {
      const j = Math.floor(rng() * (i + 1));
      [arr[i], arr[j]] = [arr[j], arr[i]];
    }
    return arr;
  }

  function inBounds(r, c) { return r >= 0 && r < rows && c >= 0 && c < cols; }

  // Step 1: perfect maze via DFS
  const cells = Array.from({ length: rows }, () =>
    Array.from({ length: cols }, () => ({
      top: true, right: true, bottom: true, left: true, visited: false,
    }))
  );

  const stack = [{ r: 0, c: 0 }];
  cells[0][0].visited = true;
  while (stack.length) {
    const { r, c } = stack[stack.length - 1];
    const nbrs = shuffle([...dirs])
      .map(d => ({ nr: r + d.dr, nc: c + d.dc, wall: d.wall, opposite: d.opposite }))
      .filter(({ nr, nc }) => inBounds(nr, nc) && !cells[nr][nc].visited);
    if (!nbrs.length) { stack.pop(); continue; }
    const { nr, nc, wall, opposite } = nbrs[0];
    cells[r][c][wall] = false;
    cells[nr][nc][opposite] = false;
    cells[nr][nc].visited = true;
    stack.push({ r: nr, c: nc });
  }
  cells.forEach(row => row.forEach(cell => delete cell.visited));

  // Step 2: find the primary path S→F via BFS
  function bfsPath(sr, sc, er, ec) {
    const visited = Array.from({ length: rows }, () => new Array(cols).fill(false));
    const prev = Array.from({ length: rows }, () => new Array(cols).fill(null));
    visited[sr][sc] = true;
    const q = [{ r: sr, c: sc }];
    while (q.length) {
      const { r, c } = q.shift();
      if (r === er && c === ec) {
        // reconstruct path
        const path = [];
        let cur = { r, c };
        while (cur) { path.push(cur); cur = prev[cur.r][cur.c]; }
        return path.reverse();
      }
      for (const d of dirs) {
        if (cells[r][c][d.wall]) continue;
        const nr = r + d.dr, nc = c + d.dc;
        if (inBounds(nr, nc) && !visited[nr][nc]) {
          visited[nr][nc] = true;
          prev[nr][nc] = { r, c };
          q.push({ r: nr, c: nc });
        }
      }
    }
    return [];
  }

  const primaryPath = bfsPath(0, 0, rows - 1, cols - 1);
  const onPrimary = new Set(primaryPath.map(p => `${p.r},${p.c}`));

  // Step 3: carve ONE deliberate second path by running a second DFS
  // from scratch on a fresh visited grid, but seeded differently so it
  // takes a different route — then open only the walls that differ from
  // the primary path, keeping the maze dense.
  //
  // Approach: BFS detour from midA to midB that ONLY opens walls that
  // are currently still closed (never touches already-open passages).
  const midA = primaryPath[Math.floor(primaryPath.length * 0.25)] ?? primaryPath[0];
  const midB = primaryPath[Math.floor(primaryPath.length * 0.75)] ?? primaryPath[primaryPath.length - 1];

  // BFS that only moves through WALLED (closed) passages — forces a new corridor
  function carveDetour(sr, sc, er, ec) {
    const visited = Array.from({ length: rows }, () => new Array(cols).fill(false));
    const prev    = Array.from({ length: rows }, () => new Array(cols).fill(null));
    visited[sr][sc] = true;
    const q = [{ r: sr, c: sc }];
    while (q.length) {
      const { r, c } = q.shift();
      if (r === er && c === ec) {
        const path = [];
        let cur = { r, c };
        while (cur) { path.push(cur); cur = prev[cur.r][cur.c]; }
        return path.reverse();
      }
      for (const d of shuffle([...dirs])) {
        const nr = r + d.dr, nc = c + d.dc;
        if (!inBounds(nr, nc) || visited[nr][nc]) continue;
        // prefer cells NOT on primary path and where the wall is still closed
        const wallClosed = cells[r][c][d.wall];
        const offPrimary = !onPrimary.has(`${nr},${nc}`);
        visited[nr][nc] = true;
        prev[nr][nc] = { r, c };
        if (wallClosed && offPrimary) q.unshift({ r: nr, c: nc }); // high priority
        else q.push({ r: nr, c: nc });
      }
    }
    return [];
  }

  const detourPath = carveDetour(midA.r, midA.c, midB.r, midB.c);

  // Only open walls between CONSECUTIVE detour cells that are currently WALLED
  // — never widen existing passages
  for (let i = 0; i < detourPath.length - 1; i++) {
    const a = detourPath[i], b = detourPath[i + 1];
    for (const d of dirs) {
      if (a.r + d.dr === b.r && a.c + d.dc === b.c) {
        if (cells[a.r][a.c][d.wall]) {   // only if wall is currently closed
          cells[a.r][a.c][d.wall]        = false;
          cells[b.r][b.c][d.opposite]    = false;
        }
        break;
      }
    }
  }

  // Step 4: add a handful of small dead-end stubs off the primary path
  // so players occasionally walk into a trap and must backtrack
  let stubs = 0;
  const stubTarget = Math.floor(Math.min(rows, cols) * 0.6);
  const shuffledPrimary = shuffle([...primaryPath.slice(2, -2)]);
  for (const { r, c } of shuffledPrimary) {
    if (stubs >= stubTarget) break;
    for (const d of shuffle([...dirs])) {
      const nr = r + d.dr, nc = c + d.dc;
      if (!inBounds(nr, nc)) continue;
      if (onPrimary.has(`${nr},${nc}`)) continue; // don't branch onto primary
      if (!cells[r][c][d.wall]) continue;         // already open
      // count how many open passages nr,nc already has
      const open = dirs.filter(x => !cells[nr][nc][x.wall]).length;
      if (open === 0) { // fully walled cell — open exactly one passage to it
        cells[r][c][d.wall]       = false;
        cells[nr][nc][d.opposite] = false;
        stubs++;
        break;
      }
    }
  }

  return cells;
}

// ─── Maze Hook ────────────────────────────────────────────────────────────────

export function useMaze(difficulty) {
  const config = MAZE_DIFFICULTY[difficulty];
  const maze = React.useMemo(() => generateMaze(config.rows, config.cols), [difficulty]);
  const start = { r: 0, c: 0 };
  const finish = { r: config.rows - 1, c: config.cols - 1 };
  return { maze, config, start, finish };
}

// ─── Move Logic ───────────────────────────────────────────────────────────────

export function tryMove(maze, pos, dir) {
  const wallMap = {
    up:    { wall: 'top', dr: -1, dc: 0 },
    down:  { wall: 'bottom', dr: 1, dc: 0 },
    left:  { wall: 'left', dr: 0, dc: -1 },
    right: { wall: 'right', dr: 0, dc: 1 },
  };
  const { wall, dr, dc } = wallMap[dir];
  if (maze[pos.r][pos.c][wall]) return null; // wall blocks
  return { r: pos.r + dr, c: pos.c + dc };
}

// ─── Maze Renderer ────────────────────────────────────────────────────────────

function MazeGrid({ maze, pos, finish, cellSize }) {
  const rows = maze.length;
  const cols = maze[0].length;
  const w = cellSize;
  const W = w * cols;
  const H = w * rows;

  const walls = [];
  maze.forEach((row, r) => {
    row.forEach((cell, c) => {
      const x = c * w;
      const y = r * w;
      if (cell.top) walls.push(`M${x},${y} L${x+w},${y}`);
      if (cell.left) walls.push(`M${x},${y} L${x},${y+w}`);
      // Draw bottom/right only for edge cells to avoid double drawing
      if (r === rows - 1 && cell.bottom) walls.push(`M${x},${y+w} L${x+w},${y+w}`);
      if (c === cols - 1 && cell.right) walls.push(`M${x+w},${y} L${x+w},${y+w}`);
    });
  });

  return (
    <div className="relative select-none" style={{ width: W, height: H }}>
      <svg width={W} height={H} className="absolute inset-0" style={{ shapeRendering: 'crispEdges' }}>
        <defs>
          <filter id="neon-glow">
            <feGaussianBlur stdDeviation="1.5" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        <rect width={W} height={H} fill="#0f0f1a" rx="8" />

        {maze.map((row, r) => row.map((_, c) => (
          <circle
            key={`d-${r}-${c}`}
            cx={c * w + w / 2}
            cy={r * w + w / 2}
            r="0.8"
            fill="rgba(139,92,246,0.15)"
          />
        )))}

        <rect
          x={finish.c * w + 1}
          y={finish.r * w + 1}
          width={w - 2}
          height={w - 2}
          fill="rgba(249,115,22,0.18)"
          rx="3"
        />

        <path
          d={walls.join(' ')}
          stroke="#7c3aed"
          strokeWidth="1.8"
          fill="none"
          filter="url(#neon-glow)"
          strokeLinecap="square"
        />

        <text
          x={w / 2}
          y={w / 2 + 4}
          textAnchor="middle"
          fontSize={w * 0.45}
          fill="rgba(139,92,246,0.5)"
        >
          S
        </text>

        <text
          x={finish.c * w + w / 2}
          y={finish.r * w + w / 2 + 4}
          textAnchor="middle"
          fontSize={w * 0.45}
          fill="rgba(249,115,22,0.9)"
        >
          F
        </text>

        <circle
          cx={pos.c * w + w / 2}
          cy={pos.r * w + w / 2}
          r={w * 0.28}
          fill="url(#player-grad)"
          filter="url(#player-glow)"
          style={{ transition: 'cx 120ms ease, cy 120ms ease' }}
        />

        <defs>
          <radialGradient id="player-grad">
            <stop offset="0%" stopColor="#fed7aa" />
            <stop offset="100%" stopColor="#f97316" />
          </radialGradient>
          <filter id="player-glow">
            <feGaussianBlur stdDeviation="2" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>
      </svg>
    </div>
  );
}

// ─── Drag Hook ────────────────────────────────────────────────────────────────

function useDrag(onMove, cellSize) {
  const origin = React.useRef(null);
  const lastCell = React.useRef(null);
  const lastDir = React.useRef(null);

  const onTouchStart = React.useCallback((e, currentPos) => {
    e.preventDefault();
    const t = e.touches[0];
    origin.current = { x: t.clientX, y: t.clientY };
    lastCell.current = { ...currentPos };
    lastDir.current = null;
  }, []);

  const onTouchMove = React.useCallback((e) => {
    e.preventDefault();
    if (!origin.current) return;

    const t = e.touches[0];
    const dx = t.clientX - origin.current.x;
    const dy = t.clientY - origin.current.y;
    const adx = Math.abs(dx);
    const ady = Math.abs(dy);

    if (adx < cellSize * 0.5 && ady < cellSize * 0.5) return;

    let dir;
    if (adx > ady * 1.3) dir = dx > 0 ? 'right' : 'left';
    else if (ady > adx * 1.3) dir = dy > 0 ? 'down' : 'up';
    else return;

    if (lastDir.current && dir !== lastDir.current) {
      origin.current = { x: t.clientX, y: t.clientY };
      lastDir.current = null;
      return;
    }

    lastDir.current = dir;

    const axisDelta = dir === 'left' || dir === 'right' ? adx : ady;
    const steps = Math.floor(axisDelta / cellSize);

    for (let i = 0; i < steps; i++) {
      const next = onMove(dir);
      if (!next) break;
      lastCell.current = next;

      if (dir === 'right') origin.current.x += cellSize;
      else if (dir === 'left') origin.current.x -= cellSize;
      else if (dir === 'down') origin.current.y += cellSize;
      else origin.current.y -= cellSize;
    }
  }, [onMove, cellSize]);

  const onTouchEnd = React.useCallback(() => {
    origin.current = null;
    lastCell.current = null;
    lastDir.current = null;
  }, []);

  return { onTouchStart, onTouchMove, onTouchEnd };
}

// ─── MazeGame Screen ──────────────────────────────────────────────────────────

export function MazeGame({ difficulty = 'Medium', streak, bestRank, onComplete, onBack }) {
  const { maze, config, start, finish } = useMaze(difficulty);

  const [pos, setPos] = React.useState(start);
  const [elapsed, setElapsed] = React.useState(0);
  const [started, setStarted] = React.useState(false);
  const [done, setDone] = React.useState(false);

  const posRef = React.useRef(start);
  const doneRef = React.useRef(false);
  const startedRef = React.useRef(false);
  const elapsedRef = React.useRef(0);

  React.useEffect(() => { posRef.current = pos; }, [pos]);
  React.useEffect(() => { doneRef.current = done; }, [done]);
  React.useEffect(() => { startedRef.current = started; }, [started]);
  React.useEffect(() => { elapsedRef.current = elapsed; }, [elapsed]);

  const [cellSize, setCellSize] = React.useState(28);
  React.useEffect(() => {
    function calc() {
      const maxW = Math.min(window.innerWidth - 32, 520);
      const maxH = window.innerHeight * 0.68;
      const byW = Math.floor(maxW / config.cols);
      const byH = Math.floor(maxH / config.rows);
      setCellSize(Math.max(20, Math.min(byW, byH, 44)));
    }
    calc();
    window.addEventListener('resize', calc);
    return () => window.removeEventListener('resize', calc);
  }, [config]);

  React.useEffect(() => {
    if (!started || done) return;
    const t = setInterval(() => setElapsed(s => s + 1), 1000);
    return () => clearInterval(t);
  }, [started, done]);

  const move = React.useCallback((dir) => {
    if (doneRef.current) return null;
    if (!startedRef.current) setStarted(true);

    const next = tryMove(maze, posRef.current, dir);
    if (!next) return null;

    posRef.current = next;
    setPos(next);

    if (next.r === finish.r && next.c === finish.c) {
      doneRef.current = true;
      setDone(true);
      setTimeout(() => onComplete({ time: elapsedRef.current + 1 }), 320);
    }

    return next;
  }, [maze, finish, onComplete]);

  React.useEffect(() => {
    const keyMap = {
      ArrowUp: 'up', ArrowDown: 'down', ArrowLeft: 'left', ArrowRight: 'right',
      w: 'up', s: 'down', a: 'left', d: 'right',
      W: 'up', S: 'down', A: 'left', D: 'right',
    };

    const handler = (e) => {
      const dir = keyMap[e.key];
      if (dir) {
        e.preventDefault();
        move(dir);
      }
    };

    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [move]);

  const dragHandlers = useDrag(move, cellSize);

  const diffColor = {
    Easy: 'bg-emerald-100 text-emerald-700',
    Medium: 'bg-amber-100 text-amber-700',
    Hard: 'bg-rose-100 text-rose-700',
  }[difficulty];

  const formatT = (s) => s < 60 ? `${s}s` : `${Math.floor(s/60)}m ${s%60}s`;

  return (
    <div
      className="flex min-h-screen flex-col"
      style={{
        background:
          'radial-gradient(circle at 10% 0%, rgba(124,58,237,0.08), transparent 35%), radial-gradient(circle at 90% 5%, rgba(249,115,22,0.07), transparent 30%), #f8fafc',
      }}
    >
      {/* ── Top Bar ── */}
      <div className="sticky top-0 z-20 border-b border-slate-200/80 bg-white/90 backdrop-blur-xl">
        <div className="mx-auto flex max-w-lg items-center gap-3 px-4 py-3">
          <button
            onClick={onBack}
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 active:scale-95 transition shadow-sm"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M19 12H5M12 5l-7 7 7 7" />
            </svg>
          </button>
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <span className="text-sm font-extrabold text-slate-900">Neon Maze</span>
                <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${diffColor}`}>{difficulty}</span>
              </div>
              <div className="flex items-center gap-1.5 rounded-full border border-orange-200 bg-orange-50 px-2.5 py-1 text-xs font-bold text-orange-500">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                  <circle cx="12" cy="12" r="10" />
                  <path d="M12 6v6l4 2" />
                </svg>
                {formatT(elapsed)}
              </div>
            </div>
          </div>
          <Link
            to="/dashboard"
            className="flex h-9 shrink-0 items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3 text-xs font-semibold text-slate-600 hover:bg-slate-50 transition shadow-sm"
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
              <polyline points="9 22 9 12 15 12 15 22" />
            </svg>
            <span className="hidden sm:inline">Dashboard</span>
          </Link>
        </div>
      </div>

      {/* ── Game Info Card ── */}
      <div className="mx-auto w-full max-w-lg px-4 pt-4">
        <div className="grid grid-cols-2 gap-2">
          {[
            { label: 'Streak',    value: streak   ? `${streak}d` : '—', isStreak: !!streak },
            { label: 'Best Rank', value: bestRank ? `#${bestRank}` : '—' },
          ].map(s => (
            <div key={s.label} className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-center shadow-sm">
              <div className="text-sm font-extrabold text-slate-900 flex items-center justify-center gap-1">
                {s.isStreak && (
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="text-orange-500">
                    <path d="M12 2s4 4 4 8a4 4 0 0 1-8 0c0-4 4-8 4-8Z" />
                    <path d="M7 14c0 4 2 8 5 8s5-4 5-8" />
                  </svg>
                )}
                {s.value}
              </div>
              <div className="text-[10px] font-semibold text-purple-500">{s.label}</div>
            </div>
          ))}
        </div>
        {!started && (
          <div className="mt-3 rounded-xl border border-purple-200 bg-purple-50 px-4 py-2.5 text-center text-xs font-semibold text-purple-600">
            <span className="sm:hidden">Drag to move · Reach the </span>
            <span className="hidden sm:inline">Arrow keys / WASD to move · Reach the </span>
            <span className="text-orange-500 font-bold">F</span> to finish
          </div>
        )}
      </div>

      {/* ── Maze Area ── */}
      <div className="flex flex-1 flex-col items-center justify-center px-3 py-4">
        <div
          className="rounded-2xl border border-purple-200 shadow-2xl shadow-purple-100 overflow-hidden"
          onTouchStart={(e) => dragHandlers.onTouchStart(e, posRef.current)}
          onTouchMove={dragHandlers.onTouchMove}
          onTouchEnd={dragHandlers.onTouchEnd}
          style={{ touchAction: 'none', userSelect: 'none' }}
        >
          <MazeGrid maze={maze} pos={pos} finish={finish} cellSize={cellSize} />
        </div>
        <div className="mt-3 text-[11px] text-slate-400">
          <span className="sm:hidden">Drag to navigate</span>
          <span className="hidden sm:inline">Arrow keys or WASD to move</span>
        </div>
      </div>
    </div>
  );
}

// ─── Mock Leaderboard Data ────────────────────────────────────────────────────

const MAZE_LEADERBOARD = {
  global: [
    { rank: 1,  name: 'Aryan Verma',  college: 'IIT Bombay',      country: 'India',   time: 38,  streak: 34, avatar: 'AV', isUser: false },
    { rank: 2,  name: 'Priya Singh',  college: 'NIT Trichy',       country: 'India',   time: 45,  streak: 21, avatar: 'PS', isUser: false },
    { rank: 3,  name: 'Dev Sharma',   college: 'BITS Pilani',      country: 'India',   time: 52,  streak: 15, avatar: 'DS', isUser: false },
    { rank: 4,  name: 'Rohit Mehta',  college: 'VIT Vellore',      country: 'India',   time: 61,  streak: 9,  avatar: 'RM', isUser: false },
    { rank: 5,  name: 'Anjali Nair',  college: 'DTU Delhi',        country: 'India',   time: 67,  streak: 12, avatar: 'AN', isUser: false },
    { rank: 6,  name: 'Kunal Joshi',  college: 'IIIT Hyderabad',   country: 'India',   time: 74,  streak: 7,  avatar: 'KJ', isUser: false },
    { rank: 47, name: 'You',          college: 'Your College',      country: 'India',   time: 96,  streak: 5,  avatar: 'YO', isUser: true  },
  ],
};

// ─── Shared Helpers ───────────────────────────────────────────────────────────

function fmt(s) {
  if (!s && s !== 0) return '—';
  if (s < 60) return `${s}s`;
  return `${Math.floor(s / 60)}m ${s % 60}s`;
}

function MazeLeaderboardRow({ row }) {
  const medalColors = ['', 'text-amber-500', 'text-slate-400', 'text-orange-400'];
  const medalColor = row.rank <= 3 ? medalColors[row.rank] : '';
  const rankBg = row.isUser
    ? 'bg-purple-50 border-purple-300'
    : 'bg-white border-slate-200 hover:border-purple-200 hover:shadow-sm';

  return (
    <div className={`flex items-center gap-3 rounded-xl border px-3 py-2.5 transition-all ${rankBg}`}>
      <span className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-bold bg-slate-100 ${medalColor || 'text-slate-500'}`}>
        {row.rank <= 3
          ? <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor" stroke="none"><path d="M12 2l2.9 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l7.1-1.01L12 2z"/></svg>
          : row.rank}
      </span>
      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-purple-600 to-orange-500 text-xs font-bold text-white shadow-sm">
        {row.avatar}
      </div>
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-semibold text-slate-900">
          {row.name} {row.isUser && <span className="text-xs text-purple-500">(You)</span>}
        </div>
        <div className="truncate text-xs text-slate-400">{row.college}</div>
      </div>
      <div className="text-right shrink-0">
        <div className="text-sm font-bold text-slate-900">{fmt(row.time)}</div>
      </div>
    </div>
  );
}

// ─── Maze Completion Screen ────────────────────────────────────────────────────

export function MazeComplete({ result, streak: streakProp, personalBest, onViewLeaderboard, onGoArena }) {
  const [rank, setRank] = React.useState(null);
  const [submitting, setSubmitting] = React.useState(true);
  const [streak, setStreak] = React.useState(streakProp);
  const isNewBest = !personalBest || result.time < personalBest;

  React.useEffect(() => {
    submitArenaResult({ gameType: 'neon-maze', completionTime: result.time })
      .then(data => {
        setRank(data.rank);
        // Fetch fresh streak after successful submit
        return getArenaStats('neon-maze').then(s => setStreak(s.currentStreak)).catch(() => {});
      })
      .catch(() => {})
      .finally(() => setSubmitting(false));
  }, []);

  const displayRank = rank;

  const [time, setTime] = React.useState(() => {
    const d = new Date();
    const midnight = new Date(d);
    midnight.setHours(24, 0, 0, 0);
    const diff = midnight - d;
    const h = Math.floor(diff / 3600000);
    const m = Math.floor((diff % 3600000) / 60000);
    const s = Math.floor((diff % 60000) / 1000);
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  });

  React.useEffect(() => {
    const t = setInterval(() => {
      const d = new Date();
      const midnight = new Date(d);
      midnight.setHours(24, 0, 0, 0);
      const diff = midnight - d;
      const h = Math.floor(diff / 3600000);
      const m = Math.floor((diff % 3600000) / 60000);
      const s = Math.floor((diff % 60000) / 1000);
      setTime(`${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`);
    }, 1000);
    return () => clearInterval(t);
  }, []);

  const handleShare = () => {
    const text = `Neon Maze Daily Challenge\nMy time: ${fmt(result.time)}${rank ? `\nRank: #${rank}` : ''}${streak ? `\nStreak: ${streak} days` : ''}\nPlay at Race Next Step!`;
    if (navigator.share) navigator.share({ text });
    else navigator.clipboard?.writeText(text);
  };

  return (
    <div
      className="flex min-h-screen flex-col items-center justify-start px-4 py-8"
      style={{
        background:
          'radial-gradient(circle at 10% 0%, rgba(124,58,237,0.08), transparent 35%), radial-gradient(circle at 90% 5%, rgba(249,115,22,0.07), transparent 30%), #f8fafc',
      }}
    >
      <div className="w-full max-w-md">
        <div className="mb-6 flex flex-col items-center">
          <div className="relative mb-4">
            <div className="flex h-24 w-24 items-center justify-center rounded-full bg-gradient-to-br from-amber-400 to-orange-500 shadow-2xl shadow-orange-200">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M6 9H4a2 2 0 0 0-2 2v0a6 6 0 0 0 6 6h0" />
                <path d="M18 9h2a2 2 0 0 1 2 2v0a6 6 0 0 1-6 6h0" />
                <rect x="6" y="2" width="12" height="13" rx="2" />
                <path d="M9 21h6" />
                <path d="M12 17v4" />
              </svg>
            </div>
            <div className="absolute -right-1 -top-1 flex h-8 w-8 items-center justify-center rounded-full bg-gradient-to-br from-purple-600 to-orange-500 text-xs font-bold text-white shadow-lg shadow-purple-200">
              {submitting ? '…' : displayRank ? `#${displayRank}` : '—'}
            </div>
          </div>
          <h1 className="text-2xl font-extrabold text-slate-900">Maze Solved!</h1>
          <p className="mt-1 text-sm text-purple-500">Today's Neon Maze · {TODAY_MAZE.difficulty}</p>
          {isNewBest && (
            <span className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-amber-50 border border-amber-300 px-3 py-1 text-xs font-bold text-amber-600">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" stroke="none"><path d="M12 2l2.9 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l7.1-1.01L12 2z"/></svg>
              New Personal Best!
            </span>
          )}
        </div>

        <div className="mb-4 grid grid-cols-3 gap-3">
          {[
            { label: 'Your Time',     value: fmt(result.time),                                   color: 'from-purple-500 to-violet-600' },
            { label: 'Global Rank',   value: submitting ? '…' : displayRank ? `#${displayRank}` : '—', color: 'from-amber-400 to-orange-500' },
            { label: 'Personal Best', value: fmt(isNewBest ? result.time : personalBest),         color: 'from-emerald-400 to-teal-500' },
          ].map(s => (
            <div key={s.label} className="rounded-2xl border border-slate-200 bg-white p-3 text-center shadow-sm">
              <div className={`mx-auto mb-2 h-1.5 w-8 rounded-full bg-gradient-to-r ${s.color}`} />
              <div className="text-lg font-extrabold text-slate-900">{s.value}</div>
              <div className="text-[11px] font-medium text-purple-500">{s.label}</div>
            </div>
          ))}
        </div>

        {streak != null && streak > 0 && (
          <div className="mb-4 rounded-2xl border border-orange-200 bg-orange-50 p-4">
            <div className="flex items-center gap-2">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-orange-500">
                <path d="M12 2s4 4 4 8a4 4 0 0 1-8 0c0-4 4-8 4-8Z" />
                <path d="M7 14c0 4 2 8 5 8s5-4 5-8" />
              </svg>
              <div>
                <div className="text-sm font-bold text-slate-900">{streak} Day Streak</div>
                <div className="text-xs text-orange-500">Keep it going!</div>
              </div>
            </div>
          </div>
        )}

        <div className="mb-4 flex flex-col gap-3">
          <button
            onClick={onViewLeaderboard}
            className="flex items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-purple-600 to-orange-500 py-3.5 text-sm font-bold text-white shadow-lg shadow-purple-200 transition hover:brightness-110"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M6 9H4a2 2 0 0 0-2 2v0a6 6 0 0 0 6 6h0" />
              <path d="M18 9h2a2 2 0 0 1 2 2v0a6 6 0 0 1-6 6h0" />
              <rect x="6" y="2" width="12" height="13" rx="2" />
              <path d="M9 21h6" />
              <path d="M12 17v4" />
            </svg>
            View Leaderboard
          </button>
          <button
            onClick={handleShare}
            className="flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white py-3.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 shadow-sm"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="18" cy="5" r="3" />
              <circle cx="6" cy="12" r="3" />
              <circle cx="18" cy="19" r="3" />
              <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
              <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
            </svg>
            Share Result
          </button>
          <button
            onClick={onGoArena}
            className="flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-transparent py-3 text-sm font-semibold text-slate-500 transition hover:text-slate-900"
          >
            ← Back to Arena
          </button>
        </div>

        <div className="flex items-center justify-center gap-2 text-xs text-slate-400">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" />
            <path d="M12 6v6l4 2" />
          </svg>
          <span>Next maze in <span className="font-bold text-purple-600">{time}</span></span>
        </div>
      </div>
    </div>
  );
}

// ─── Maze Leaderboard View ────────────────────────────────────────────────────

export function MazeLeaderboard({ onBack }) {
  const [rows, setRows] = React.useState([]);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    getArenaLeaderboard('neon-maze')
      .then(data => setRows(data.leaderboard || []))
      .catch(() => setRows(MAZE_LEADERBOARD.global))
      .finally(() => setLoading(false));
  }, []);

  const top3 = rows.slice(0, 3);

  return (
    <div
      className="flex min-h-screen flex-col"
      style={{
        background: 'radial-gradient(circle at 10% 0%, rgba(124,58,237,0.08), transparent 35%), radial-gradient(circle at 90% 5%, rgba(249,115,22,0.07), transparent 30%), #f8fafc',
        overflowY: 'auto',
      }}
    >
      <div className="sticky top-0 z-20 border-b border-slate-200/80 bg-white/90 backdrop-blur-xl">
        <div className="mx-auto flex max-w-lg items-center gap-3 px-4 py-3">
          <button
            onClick={onBack}
            className="flex h-9 w-9 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 transition shadow-sm"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M19 12H5M12 5l-7 7 7 7" />
            </svg>
          </button>
          <div>
            <div className="text-sm font-extrabold text-slate-900">Leaderboard</div>
            <div className="text-xs text-purple-500">Today · Neon Maze · {TODAY_MAZE.difficulty}</div>
          </div>
        </div>
      </div>

      <div className="mx-auto w-full max-w-lg px-4 py-5">
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="h-8 w-8 animate-spin rounded-full border-2 border-purple-500 border-t-transparent" />
          </div>
        ) : (
          <>
            {top3.length === 3 && (
              <div className="mb-5 flex items-end justify-center gap-4">
                {[top3[1], top3[0], top3[2]].map((row, i) => {
                  const heights = ['h-20', 'h-28', 'h-16'];
                  const podiumBg = [
                    'bg-slate-100 border-slate-200',
                    'bg-gradient-to-b from-purple-100 to-orange-50 border-purple-200',
                    'bg-orange-50 border-orange-200',
                  ];
                  const medals = [
                    <svg key="m2" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="8" r="2" />
                      <path d="M6 21c0-3 2-6 6-6s6 3 6 6" />
                    </svg>,
                    <svg key="m1" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M6 3l6 3 6-3-6 7-6-7Z" />
                      <circle cx="12" cy="15" r="4" />
                    </svg>,
                    <svg key="m3" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="8" r="2" />
                      <path d="M8 21c0-2 1-5 4-5s4 3 4 5" />
                    </svg>,
                  ];

                  return (
                    <div key={row.rank} className="flex flex-col items-center gap-2">
                      <div className="flex h-11 w-11 items-center justify-center rounded-full bg-gradient-to-br from-purple-600 to-orange-500 text-sm font-bold text-white shadow-md shadow-purple-200">
                        {(row.name || 'A').slice(0, 2).toUpperCase()}
                      </div>
                      <div className="text-center">
                        <div className="text-[11px] font-bold text-slate-900 max-w-[72px] truncate">{(row.name || 'Anonymous').split(' ')[0]}</div>
                        <div className="text-[10px] text-orange-500 font-semibold">{fmt(row.completionTime)}</div>
                      </div>
                      <div
                        className={`flex w-20 ${heights[i]} items-start justify-center rounded-t-xl pt-2 text-lg border ${podiumBg[i]}`}
                      >
                        {medals[i]}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            <div className="space-y-2">
              {rows.map((row, i) => (
                <MazeLeaderboardRow
                  key={i}
                  row={{
                    ...row,
                    time: row.completionTime,
                    avatar: (row.name || 'AN').slice(0, 2).toUpperCase(),
                    isUser: row.isUser || false,
                  }}
                />
              ))}
            </div>
          </>
        )}

        <div className="mt-5 rounded-2xl border border-purple-200 bg-gradient-to-br from-purple-50 to-orange-50 p-4 text-center">
          <p className="mb-3 text-sm text-slate-600">Sign in to appear on the leaderboard and save your rank</p>
          <a
            href="/signup"
            className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-purple-600 to-orange-500 px-5 py-2.5 text-sm font-bold text-white shadow-md shadow-purple-200"
          >
            Join Now →
          </a>
        </div>
      </div>
    </div>
  );
}

